import { useId, useLayoutEffect, useRef } from 'react';
import { minisiteMenuPosition } from './minisiteMenuPosition';

export function useMinisiteMenu(open, onClose) {
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const id = useId();
  const menuId = `ms-menu-${id.replace(/[^a-zA-Z0-9_-]/g, '')}`;

  useLayoutEffect(() => {
    if (!open) return;
    const trigger = triggerRef.current;
    const menu = menuRef.current;
    if (!trigger || !menu) return;

    // A scoped stylesheet keeps measured coordinates out of inline style attributes.
    const positionStyle = document.createElement('style');
    positionStyle.dataset.minisiteMenu = menuId;
    document.head.appendChild(positionStyle);
    const viewport = window.visualViewport;
    const updatePosition = () => {
      const bounds = {
        left: viewport?.offsetLeft || 0, top: viewport?.offsetTop || 0,
        width: viewport?.width || window.innerWidth,
        height: viewport?.height || window.innerHeight,
      };
      const anchor = trigger.getBoundingClientRect();
      if (anchor.bottom <= bounds.top || anchor.top >= bounds.top + bounds.height ||
          anchor.right <= bounds.left || anchor.left >= bounds.left + bounds.width) {
        onClose();
        return;
      }
      const width = Math.min(215, Math.max(1, bounds.width - 16));
      positionStyle.textContent = `#${menuId}{width:${width}px;max-height:none;}`;
      const position = minisiteMenuPosition(anchor, menu.getBoundingClientRect().height,
        bounds, window.getComputedStyle(trigger).direction === 'rtl');
      positionStyle.textContent = `#${menuId}{left:${position.left}px;top:${position.top}px;width:${position.width}px;max-height:${position.maxHeight}px;visibility:visible;}`;
    };
    const dismissOutside = event => {
      if (!trigger.contains(event.target) && !menu.contains(event.target)) onClose();
    };
    const dismissOnScroll = event => {
      if (event.target === window || !menu.contains(event.target)) onClose();
    };
    const dismissOnEscape = event => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      onClose();
      trigger.focus({ preventScroll: true });
    };

    updatePosition();
    document.addEventListener('mousedown', dismissOutside);
    document.addEventListener('touchstart', dismissOutside, { passive: true });
    document.addEventListener('keydown', dismissOnEscape);
    window.addEventListener('scroll', dismissOnScroll, true);
    window.addEventListener('resize', updatePosition);
    viewport?.addEventListener('resize', updatePosition);
    viewport?.addEventListener('scroll', onClose);
    return () => {
      positionStyle.remove();
      document.removeEventListener('mousedown', dismissOutside);
      document.removeEventListener('touchstart', dismissOutside);
      document.removeEventListener('keydown', dismissOnEscape);
      window.removeEventListener('scroll', dismissOnScroll, true);
      window.removeEventListener('resize', updatePosition);
      viewport?.removeEventListener('resize', updatePosition);
      viewport?.removeEventListener('scroll', onClose);
    };
  }, [open, onClose, menuId]);

  return { triggerRef, menuRef, menuId };
}
